import React, {useMemo, useEffect, useState} from "react";
import {Box, Typography, Tooltip, Skeleton, Button, Stack} from "@mui/material";
import {DataGrid} from "@mui/x-data-grid";
import MiniBars from "./MiniBars";
import {SeverityDot, ShareBarCell, LastCell, DeltaCell, IdxCell, StatusChip, YdCell, YdTip} from "./cells";
import {
    fmtCompact, fmtPct0, fmtPct, fmtMoneyShort2, fmtDay, fmtDow, fmtRange, reasonOf, statusMeta, severityOf, EMPTY, isNilValue, DAY_METRICS,
    COMPARE_BASES,
} from "./format";
import {TYPE, TABULAR, NEUTRAL, SEV, GRID_ROW_H, GRID_HEADER_H, heatFor} from "./tokens";

const num = {type: "number", align: "right", headerAlign: "right"};
const mutedText = (v, title) => <Typography component="span" title={title} sx={{...TYPE.muted, ...TABULAR}}>{v}</Typography>;

/** Column descriptions (brief v2.2 §1) — shown as a tooltip on the header. */
export const COLUMN_HELP = {
    provider: "Demand partner that pays for the tag",
    fs_day: "Searches Tiger sent to the site (per day, avg over the range)",
    mon_day: "Searches counted (monetized) by the partner, per day",
    coverage: "Monetized ÷ sent. >100% when a search yields several monetized pages",
    clicks_day: "Ad clicks per day",
    ctr: "Ad clicks ÷ monetized searches",
    rpm: "Revenue per 1,000 monetized searches",
    rpm_fs: "Revenue per 1,000 searches sent — what 1k of our traffic earns here (use to allocate traffic)",
    idx: "$/1k sent vs the median tag of the same network (×1.0 = typical)",
    rev: "Revenue in the range",
    rev_last: "Revenue on the last closed day",
    delta_rev_pct: "Revenue vs previous range",
    yest: "Yesterday vs 7-day avg (or vs day before, per the toggle). Coloured when the move is outside the tag's usual range",
    trend: "Daily revenue over the range",
};

export const TABLE_HELP = [
    "Sent = searches Tiger sent · Cov % = counted by the partner ÷ sent.",
    "RPM = revenue per 1k counted · $/1k sent = per 1k sent (use to allocate traffic).",
    "× network = vs median of the same network · Δ prev = vs previous range · Δ yday = yesterday vs 7-day median.",
    "Rows with a red edge need attention; grey rows had nothing to measure. The thick rule marks 80% of revenue.",
    "Day-by-day values live in the Heatmap view (header switch).",
];

/**
 * Column definitions for one variant: "desktop" (≥ 1200), "mid" (900–1199), "tablet" (600–899).
 * Provider / CTR / RPM are always defined (so the CSV export keeps them) and hidden through the visibility model.
 */
const buildColumns = ({variant, rangeDays, days, maxRev, trendMode, metric, dayTotals, compareBase, range}) => {
    const tablet = variant === "tablet";
    const daysMode = trendMode === "days";
    const ydMode = trendMode === "yday";
    const dayWidth = tablet && daysMode ? 54 : 68; // tablet: 7 day columns must fit a 744 px scroller
    const lastDay = days.length ? days[days.length - 1] : (range && range.to) || null;
    const prevLabel = range && range.prev_from ? fmtRange(range.prev_from, range.prev_to) : "previous range";
    const cols = [];

    if (tablet) {
        cols.push({
            field: "sev", headerName: "Status", width: 112, sortable: true, disableExport: true, // 112: "NOT REPORTED" chip fits
            valueGetter: (p) => p.row._sevKey,
            renderCell: (p) => {
                const sev = severityOf(p.row.status);
                if (sev === "ok" || sev === "idle") return null;
                return (
                    <Tooltip title={`${statusMeta(p.row.status).label} · ${reasonOf(p.row)}`} placement="right" arrow>
                        <Box sx={{display: "flex", alignItems: "center", gap: 0.75, height: "100%"}}>
                            <Box sx={{width: 4, height: 20, borderRadius: 1, bgcolor: (t) => SEV[sev].dot(t)}}/>
                            <StatusChip status={p.row.status} short/>
                        </Box>
                    </Tooltip>
                );
            },
        });
    } else {
        cols.push({
            field: "sev", headerName: "", width: 28, minWidth: 28, sortable: true, disableExport: true, disableColumnMenu: true,
            valueGetter: (p) => p.row._sevKey,
            renderHeader: () => <Box component="span" sx={{width: 8}} aria-label="Severity"/>,
            renderCell: (p) => {
                const dot = <SeverityDot status={p.row.status}/>;
                if (!dot) return null;
                return (
                    <Tooltip title={`${statusMeta(p.row.status).label} · ${reasonOf(p.row)}`} placement="right" arrow>
                        <Box sx={{display: "flex", alignItems: "center", height: "100%"}}>{dot}</Box>
                    </Tooltip>
                );
            },
        });
    }

    cols.push({
        field: "tag", headerName: "Tag", width: tablet ? 60 : 68,
        renderCell: (p) => <Typography component="span" sx={TYPE.tag}>{p.value}</Typography>,
    });
    cols.push({
        field: "site", headerName: "Site", flex: 1, minWidth: tablet ? 100 : variant === "mid" ? 120 : 140,
        valueGetter: (p) => p.value || "",
        renderCell: (p) => (
            <Tooltip title={`${p.value || EMPTY}${p.row.supply_feed_name ? ` · ${p.row.supply_feed_name}` : ""}`} placement="top-start" enterDelay={600}>
                <Typography component="span" noWrap sx={{...TYPE.muted, display: "block", width: "100%"}}>{p.value || EMPTY}</Typography>
            </Tooltip>
        ),
    });
    cols.push({
        field: "provider", headerName: "Partner", description: COLUMN_HELP.provider, width: tablet ? 130 : 150, valueGetter: (p) => p.row.provider || "",
        renderCell: (p) => <Typography component="span" noWrap title={p.row.provider || ""} sx={{...TYPE.muted, display: "block", width: "100%"}}>{p.row._provider}</Typography>,
    });
    if (!tablet) {
        cols.push({field: "fs_day", headerName: "Sent/d", description: COLUMN_HELP.fs_day, width: 76, ...num, valueGetter: (p) => (rangeDays ? p.row.fs / rangeDays : null), renderCell: (p) => mutedText(fmtCompact(p.value))});
        cols.push({field: "mon_day", headerName: "Monetized/d", description: COLUMN_HELP.mon_day, width: 76, ...num, valueGetter: (p) => (rangeDays ? p.row.mon / rangeDays : null), renderCell: (p) => mutedText(fmtCompact(p.value))});
    }
    cols.push({
        field: "coverage", headerName: "Cov %", description: COLUMN_HELP.coverage, width: tablet ? 72 : 64, ...num,
        valueGetter: (p) => (p.row.fs > 0 ? p.row.coverage : null),
        renderCell: (p) => mutedText(isNilValue(p.value) ? EMPTY : fmtPct0(p.value), p.value > 1 ? "Over 100%: a search yields several monetized pages" : undefined),
    });
    if (!tablet) {
        cols.push({field: "clicks_day", headerName: "Clicks/d", description: COLUMN_HELP.clicks_day, width: 76, ...num, valueGetter: (p) => (rangeDays ? p.row.clicks / rangeDays : null), renderCell: (p) => mutedText(fmtCompact(p.value))});
    }
    if (!tablet) {
        cols.push({field: "ctr", headerName: "CTR", description: COLUMN_HELP.ctr, width: 60, ...num, renderCell: (p) => mutedText(fmtPct(p.value))});
        cols.push({field: "rpm", headerName: "RPM", description: COLUMN_HELP.rpm, width: 68, ...num, renderCell: (p) => mutedText(fmtMoneyShort2(p.value))});
    }
    cols.push({
        field: "rpm_fs", headerName: "$/1k sent", description: COLUMN_HELP.rpm_fs, width: 84, ...num,
        // "$0.00" on an idle row reads like a measured yield; "—" says there was nothing to measure.
        renderCell: (p) => (
            <Typography component="span" sx={{fontSize: 13, fontWeight: 600, ...TABULAR, color: (Number(p.row.rev) || 0) === 0 ? "text.disabled" : "inherit"}}>
                {(Number(p.row.rev) || 0) === 0 ? EMPTY : fmtMoneyShort2(p.value)}
            </Typography>
        ),
    });
    if (!tablet) {
        cols.push({field: "idx", headerName: "× network", description: COLUMN_HELP.idx, width: 78, ...num, valueGetter: (p) => p.row._idx, renderCell: (p) => <IdxCell value={p.value} severity={p.row._sev}/>});
    }
    cols.push({
        field: "rev", headerName: tablet ? "Rev" : "Revenue", description: COLUMN_HELP.rev, width: tablet ? 90 : 116, ...num,
        renderCell: (p) => <ShareBarCell value={p.value} max={maxRev} muted={p.row._sev === "idle"}/>,
    });
    if (!tablet) {
        cols.push({
            field: "rev_last", headerName: lastDay ? fmtDay(lastDay) : "Last day", description: COLUMN_HELP.rev_last, width: 66, ...num,
            renderCell: (p) => <LastCell value={p.value} partial={p.row.partial_last_day} risk={p.row._atRisk} tip={p.row.yesterday ? <YdTip y={p.row.yesterday} base={compareBase}/> : null}/>,
        });
    }
    cols.push({
        field: "delta_rev_pct", headerName: "Δ prev", description: `Revenue vs previous range (${prevLabel})`, width: tablet ? 68 : 66, ...num,
        // Colour budget: on alarm rows (status carries the colour) and in yesterday mode (the day delta does) the week delta turns grey.
        renderCell: (p) => <DeltaCell value={p.value} prev={p.row.rev_prev} cur={p.row.rev} severity={p.row._sev} sx={ydMode ? {color: "text.secondary", fontWeight: 400} : undefined}/>,
    });

    // Δ yday (v2.1/v2.2). Both baselines are defined so the CSV carries them; the visible one follows the compare control.
    const yd = (p) => p.row.yesterday || null;
    COMPARE_BASES.forEach((b) => {
        cols.push({
            field: `yest_${b.key}`, headerName: `Δ yday vs ${b.label}`, description: COLUMN_HELP.yest, width: 72, ...num, // 72: "◌ not yet" fits
            valueGetter: (p) => (yd(p) && yd(p).state !== "pending" && !isNilValue(yd(p)[b.field]) ? Number(yd(p)[b.field]) : null),
            renderHeader: () => <Box component="span" sx={{fontSize: 12, fontWeight: 600, color: "text.secondary"}}>Δ yday</Box>,
            renderCell: (p) => <YdCell y={yd(p)} base={b.key}/>,
        });
    });

    if (daysMode && days.length) {
        const m = DAY_METRICS.find((x) => x.key === metric) || DAY_METRICS[0];
        days.forEach((day, i) => {
            cols.push({
                field: `d_${day}`, headerName: fmtDay(day), width: dayWidth, ...num, sortable: true,
                valueGetter: (p) => (p.row.daily && p.row.daily[i] ? p.row.daily[i][m.key] : null),
                renderHeader: () => (
                    <Box sx={{textAlign: "right", lineHeight: 1.15, width: "100%"}}>
                        <Typography component="div" sx={{fontSize: 12, fontWeight: 600, ...TABULAR}} title={fmtDow(day)}>{fmtDay(day)}</Typography>
                        <Typography component="div" sx={{fontSize: 11, color: "text.secondary", fontWeight: 400, ...TABULAR}}>{m.fmt(dayTotals[i])}</Typography>
                    </Box>
                ),
                renderCell: (p) => {
                    const d = p.row.daily && p.row.daily[i];
                    if (!d || (!(d.fs > 0) && !(d.mon > 0) && !(d.rev > 0))) {
                        return <Typography component="span" sx={{color: NEUTRAL.zeroDot, fontSize: 13}}>·</Typography>;
                    }
                    // Heat only for days with traffic (NO_TIGER tags show their values untinted).
                    const tint = d.fs > 0 && p.row._earningDays >= 3 ? heatFor(d[m.key], p.row._means[m.key]) : null;
                    return (
                        <Box sx={{width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "flex-end", bgcolor: tint || "transparent", px: 1, mx: -1, boxSizing: "content-box"}}>
                            <Typography component="span" sx={{fontSize: 12.5, ...TABULAR, color: "text.primary"}}>{m.fmt(d[m.key])}</Typography>
                        </Box>
                    );
                },
            });
        });
    } else {
        cols.push({
            field: "trend", headerName: "Daily rev", description: COLUMN_HELP.trend, width: tablet ? 90 : 92, sortable: false, disableExport: true, align: "right", headerAlign: "right",
            renderCell: (p) => (
                <Box sx={{display: "flex", alignItems: "center", height: "100%"}}>
                    <MiniBars daily={p.row.daily} width={tablet ? 80 : 84} height={20} partialLast={p.row.partial_last_day}
                              label={`${p.row.tag}: daily revenue, ${rangeDays} days`}/>
                </Box>
            ),
        });
    }
    return cols;
};

const gridSx = {
    border: 0, ...TABULAR, fontSize: 13,
    "& .MuiDataGrid-columnHeaders": {bgcolor: NEUTRAL.headerBg, borderBottom: `1px solid ${NEUTRAL.divider}`, borderRadius: 0},
    "& .MuiDataGrid-columnHeaderTitle": {fontSize: 12, fontWeight: 600, color: "text.secondary"},
    "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {px: 1, outline: "none !important"},
    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {outline: "none"},
    "& .MuiDataGrid-columnSeparator": {display: "none"},
    "& .MuiDataGrid-iconButtonContainer .MuiSvgIcon-root": {fontSize: 14},
    "& .MuiDataGrid-cell": {borderBottom: `1px solid ${NEUTRAL.hairline}`, lineHeight: 1.2},
    "& .MuiDataGrid-row": {cursor: "pointer"},
    "& .MuiDataGrid-row:hover": {bgcolor: NEUTRAL.hover},
    "& .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover": {bgcolor: NEUTRAL.selected, boxShadow: NEUTRAL.selectedInset},
    "& .MuiDataGrid-row.tc-row--error": {boxShadow: "inset 2px 0 0 #d32f2f"},
    "& .MuiDataGrid-row.tc-row--warning": {boxShadow: "inset 2px 0 0 #ed6c02"},
    "& .MuiDataGrid-row.tc-row--error.Mui-selected": {boxShadow: "inset 3px 0 0 #d32f2f"},
    "& .MuiDataGrid-row.tc-row--idle": {color: NEUTRAL.idleText, "& .MuiTypography-root": {color: NEUTRAL.idleText}},
    "& .MuiDataGrid-row.tc-row--cutoff .MuiDataGrid-cell": {borderBottom: "2px solid #bdbdbd"},
    "& .MuiDataGrid-virtualScroller": {overflowX: "auto"},
    "& .MuiDataGrid-overlay": {bgcolor: "transparent"},
    "& .MuiDataGrid-footerContainer": {minHeight: 40, borderTop: `1px solid ${NEUTRAL.divider}`},
    "& .MuiTablePagination-root": {fontSize: 12.5, color: "text.secondary", overflow: "visible"},
    "& .MuiTablePagination-toolbar": {minHeight: 40, pl: 1},
    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {fontSize: 12.5, m: 0, ...TABULAR},
    "& .MuiTablePagination-select": {fontSize: 12.5, ...TABULAR},
};

/**
 * The one table. `rows` are already filtered; `sortModel` is controlled by the page so the 80 % cutoff rule and the
 * drawer prev/next follow the same order.
 */
const TagGrid = ({
    rows, days, rangeDays, maxRev, cutoffTag, sortModel, onSortModelChange, trendMode, dayMetric, dayTotals,
    apiRef, onSelectTag, selectedTag, variant, firstLoad, emptyText, onClearFilters, hideRpm, compareBase, range,
    paginationModel, onPaginationModelChange, pageSizeOptions = [50, 100],
}) => {
    const columns = useMemo(
        () => buildColumns({variant, rangeDays, days, maxRev, trendMode, metric: dayMetric, dayTotals, compareBase, range}),
        [variant, rangeDays, days, maxRev, trendMode, dayMetric, dayTotals, compareBase, range],
    );
    const daysMode = trendMode === "days";
    const ydMode = trendMode === "yday";
    const tablet = variant === "tablet";
    /**
     * Hidden (not removed) columns, so `exportDataAsCsv({allColumns: true})` still writes them.
     * Days mode drops Provider / CTR / RPM; "mid" drops Provider and (below 1024 px) RPM. Tablet shows Partner (130) but not
     * Cov % so the 744 px scroller fits without scrolling: 112 + 60 + 100 + 130 + 84 + 90 + 68 + 90 = 734 ≤ 744; tablet + days
     * also drops Δ and $/1k sent so seven 54 px day columns fit: 112 + 60 + 100 + 90 + 7×54 = 740 ≤ 744.
     */
    const columnVisibilityModel = useMemo(() => ({
        mon_day: false, clicks_day: false,
        provider: (variant === "desktop" || tablet) && !daysMode,
        ctr: variant === "desktop" && !daysMode,
        rpm: !hideRpm && !daysMode,
        coverage: !tablet,
        delta_rev_pct: !(tablet && daysMode),
        rpm_fs: !(tablet && daysMode),
        // Yesterday mode: the trend bars give way to Δ yday (of the chosen baseline; the other stays for the CSV); the dated last-day column stays.
        trend: !ydMode,
        yest_base: ydMode && compareBase !== "d1",
        yest_d1: ydMode && compareBase === "d1",
    }), [variant, daysMode, ydMode, compareBase, hideRpm, tablet]);
    const rowHeight = tablet ? 44 : GRID_ROW_H;
    const headerHeight = daysMode ? 44 : GRID_HEADER_H;

    // Right-edge fade while the virtual scroller can still scroll to the right (days mode on narrow screens).
    const [canScrollRight, setCanScrollRight] = useState(false);
    const gridReady = !firstLoad && rows.length > 0;
    useEffect(() => {
        if (!gridReady) return undefined;
        const api = apiRef.current || {};
        const root = api.rootElementRef && api.rootElementRef.current;
        const el = (api.virtualScrollerRef && api.virtualScrollerRef.current) || (root && root.querySelector(".MuiDataGrid-virtualScroller"));
        if (!el) return undefined;
        const update = () => setCanScrollRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 1);
        update();
        el.addEventListener("scroll", update, {passive: true});
        // Observe the content too: adding day columns widens the content, not the scroller itself.
        const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
        if (ro) {
            ro.observe(el);
            const content = el.querySelector(".MuiDataGrid-virtualScrollerContent");
            if (content) ro.observe(content);
        }
        return () => { el.removeEventListener("scroll", update); if (ro) ro.disconnect(); };
    }, [apiRef, gridReady, columns, columnVisibilityModel, rows]);

    if (firstLoad) {
        return (
            <Box sx={{px: 1}} aria-busy="true" aria-label="Loading tags">
                {Array.from({length: 12}).map((_, i) => <Skeleton key={i} height={rowHeight - 6} sx={{my: "3px", transform: "none"}}/>)}
            </Box>
        );
    }
    if (!rows.length) {
        return (
            <Stack alignItems="center" spacing={1} sx={{py: 6}}>
                <Typography sx={{fontSize: 14, color: "text.secondary"}}>{emptyText || "No tags match these filters"}</Typography>
                {onClearFilters && <Button size="small" variant="outlined" onClick={onClearFilters} sx={{textTransform: "none"}}>Clear filters</Button>}
            </Stack>
        );
    }

    return (
        <Box sx={{
            width: "100%", minWidth: 0, position: "relative",
            "&::after": {
                content: '""', position: "absolute", top: 0, right: 0, bottom: 0, width: 40, pointerEvents: "none",
                background: "linear-gradient(to right, rgba(255,255,255,0), #fff 85%)",
                opacity: canScrollRight ? 1 : 0, transition: "opacity .15s",
            },
        }}>
            <DataGrid
                apiRef={apiRef}
                rows={rows}
                columns={columns}
                getRowId={(r) => r.tag}
                autoHeight
                pagination
                hideFooter={rows.length <= pageSizeOptions[0]}
                pageSizeOptions={pageSizeOptions}
                paginationModel={paginationModel}
                onPaginationModelChange={onPaginationModelChange}
                disableColumnMenu
                disableRowSelectionOnClick
                rowHeight={rowHeight}
                columnHeaderHeight={headerHeight}
                sortModel={sortModel}
                onSortModelChange={onSortModelChange}
                sortingOrder={["desc", "asc"]}
                columnVisibilityModel={columnVisibilityModel}
                rowSelectionModel={selectedTag ? [selectedTag] : []}
                onRowClick={(p) => onSelectTag(p.row.tag)}
                getRowClassName={(p) => {
                    const cls = [];
                    if (p.row._sev === "error" || p.row._sev === "warning") cls.push(`tc-row--${p.row._sev}`);
                    if (p.row._sev === "idle") cls.push("tc-row--idle");
                    if (cutoffTag && p.id === cutoffTag) cls.push("tc-row--cutoff");
                    return cls.join(" ");
                }}
                sx={gridSx}
            />
        </Box>
    );
};

export default TagGrid;
